/**
 * Like-button optimistic reducer (PURE). The play page's like button updates
 * the UI IMMEDIATELY on click (optimistic), fires the like/unlike write, and
 * reverts on failure. This module holds the state machine so the optimistic
 * flow — including the revert-on-error path — is unit-tested without React (the
 * spec lists "like button optimistic flow" as a required test).
 *
 * The button is auth-gated: an anonymous/guest user can't like. That gate lives
 * in the component, but {@link likeReducer} models the `blocked` action so a
 * click while signed-out is representable and testable.
 */

/** The like button's state. */
export interface LikeState {
  /** Whether the current user likes this creation. */
  liked: boolean;
  /** Displayed like count. */
  count: number;
  /** A write is in flight (button disabled). */
  pending: boolean;
  /** Set when the last action was blocked (e.g. not signed in). */
  blockedReason?: string;
}

/** Actions the reducer folds. */
export type LikeAction =
  /** User clicked the button (optimistic toggle). */
  | { type: 'toggle' }
  /** The in-flight write succeeded — clears pending, keeps optimistic value. */
  | { type: 'settled' }
  /** The in-flight write failed — revert the optimistic toggle. */
  | { type: 'failed' }
  /** Click while not allowed (signed out). */
  | { type: 'blocked'; reason: string }
  /** A fresh server value arrived (onSnapshot) — reconcile count/liked. */
  | { type: 'sync'; liked: boolean; count: number };

/** Initial state from the server-known values. */
export function initLikeState(liked: boolean, count: number): LikeState {
  return { liked, count, pending: false };
}

/**
 * Fold an action into the like state. `toggle` optimistically flips `liked` and
 * moves the count by ±1, marks `pending`. `failed` reverts the flip. `settled`
 * just clears `pending`. `sync` trusts the server for count but preserves an
 * in-flight optimistic `liked` (so a mid-write snapshot doesn't flicker).
 */
export function likeReducer(state: LikeState, action: LikeAction): LikeState {
  switch (action.type) {
    case 'toggle': {
      // A no-op while a write is already in flight (button should be disabled,
      // but guard anyway so double-clicks can't double-count).
      if (state.pending) return state;
      const liked = !state.liked;
      const count = Math.max(0, state.count + (liked ? 1 : -1));
      return { liked, count, pending: true, blockedReason: undefined };
    }
    case 'settled':
      return { ...state, pending: false };
    case 'failed': {
      // Revert the optimistic toggle.
      const liked = !state.liked;
      const count = Math.max(0, state.count + (liked ? 1 : -1));
      return { liked, count, pending: false };
    }
    case 'blocked':
      return { ...state, blockedReason: action.reason };
    case 'sync': {
      // While a write is pending, keep the optimistic liked flag but accept the
      // server's count minus/plus our pending delta is complex — simplest robust
      // rule: if not pending, fully trust the server; if pending, keep our
      // optimistic view untouched.
      if (state.pending) return state;
      return { liked: action.liked, count: Math.max(0, action.count), pending: false };
    }
    default:
      return state;
  }
}
